import React, { useState } from 'react';
import { EyeIcon, EyeOffIcon, XIcon } from '../../icons';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  helperText?: string;
  error?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  inputSize?: 'md' | 'lg';
  showClearButton?: boolean;
  onClear?: () => void;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      leadingIcon,
      trailingIcon,
      inputSize = 'md',
      showClearButton = false,
      onClear,
      id,
      required,
      disabled,
      type = 'text',
      className = '',
      value,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    const sizeStyles = { md: 'h-11 px-3 py-2 text-base', lg: 'h-13 px-4 py-3 text-lg' };
    const baseStyles =
      'w-full rounded-md border transition-colors duration-150 focus:outline-none focus:ring-2 text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-900 disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:text-neutral-400 dark:disabled:text-neutral-500 disabled:cursor-not-allowed';
    const stateStyles = error
      ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20'
      : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500 focus:border-primary-500 focus:ring-primary-500/20';
    const hasTrailing = trailingIcon || showClearButton || isPassword;

    return (
      <div className={className}>
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5"
        >
          {label}
          {required && <span className="text-danger-500 ml-1">*</span>}
        </label>
        <div className="relative">
          {leadingIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">
              {leadingIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            required={required}
            disabled={disabled}
            value={value}
            aria-invalid={!!error}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            aria-required={required}
            className={`${baseStyles} ${stateStyles} ${sizeStyles[inputSize]} ${leadingIcon ? 'pl-10' : ''} ${hasTrailing ? 'pr-10' : ''}`}
            {...props}
          />
          {hasTrailing && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {showClearButton && value && !disabled && (
                <button
                  type="button"
                  onClick={onClear}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Clear input"
                >
                  <XIcon size="md" />
                </button>
              )}
              {isPassword && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-neutral-400 hover:text-neutral-600 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  data-testid="password-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon size="md" /> : <EyeIcon size="md" />}
                </button>
              )}
              {trailingIcon && <div className="text-neutral-500">{trailingIcon}</div>}
            </div>
          )}
        </div>
        {error ? (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-danger-600" role="alert">
            {error}
          </p>
        ) : helperText ? (
          <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-neutral-600">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';
